import React, { useState, useEffect } from 'react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PDFJSViewer.scss';
import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from './icons';
import { ESSButton as Button } from '../ESSButton';

interface PDFViewerProps extends React.HTMLAttributes<HTMLDivElement> {
  url: string;
  width?: string | number;
  height?: string | number;
  /** Preferred download filename (e.g. Resource title). Falls back to uploaded file name. */
  downloadFilename?: string;
}

const PDFJSViewer: React.FC<PDFViewerProps> = ({
  url,
  style,
  width = '100%',
  height = '900px',
  className = '',
  downloadFilename,
  ...props
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageInput, setPageInput] = useState<string>('1');
  const [scale, setScale] = useState<number>(1.0);
  const [scaleInput, setScaleInput] = useState<string>('100%');
  const [PDFComponents, setPDFComponents] = useState<{
    Document: any;
    Page: any;
    pdfjs: any;
  } | null>(null);

  // Dynamically import react-pdf ONLY on client
  useEffect(() => {
    import('react-pdf')
      .then((reactPdf) => {
        const pdfjsLib = reactPdf.pdfjs; // Store reference
        // Dynamic import - Volto bundler handles path automatically
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url,
        ).href;
        setPDFComponents({
          Document: reactPdf.Document,
          Page: reactPdf.Page,
          pdfjs: pdfjsLib, // ← Include pdfjs
        });
      })
      .catch(console.error);
  }, []);

  // Show loading until components load
  if (!PDFComponents) {
    return <div className="pdf-loading">Loading PDF viewer...</div>;
  }

  const { Document, Page } = PDFComponents;

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    //console.log('PDF loaded successfully with', numPages, 'pages');
    setNumPages(numPages);
    setPageNumber(1);
    setPageInput('1');
  };

  const changePage = (offset: number) => {
    const newPage = pageNumber + offset;
    if (newPage >= 1 && newPage <= numPages) {
      setPageNumber(newPage);
      setPageInput(String(newPage));
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= numPages) {
      setPageNumber(page);
      setPageInput(String(page));
    }
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  const handlePageInputBlur = () => {
    const page = parseInt(pageInput, 10);
    if (!isNaN(page)) {
      goToPage(page);
    } else {
      setPageInput(String(pageNumber));
    }
  };

  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const page = parseInt(pageInput, 10);
      if (!isNaN(page)) {
        goToPage(page);
      }
    }
  };

  const zoomIn = () => {
    setScale((prevScale) => {
      const newScale = Math.min(prevScale + 0.25, 3.0);
      setScaleInput(`${Math.round(newScale * 100)}%`);
      return newScale;
    });
  };

  const zoomOut = () => {
    setScale((prevScale) => {
      const newScale = Math.max(prevScale - 0.25, 0.25);
      setScaleInput(`${Math.round(newScale * 100)}%`);
      return newScale;
    });
  };

  const resetZoom = () => {
    setScale(1.0);
    setScaleInput('100%');
  };

  const handleScaleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScaleInput(e.target.value);
  };

  const changeValidateScale = () => {
    const percentageMatch = scaleInput.match(/(\d+)/);
    if (percentageMatch) {
      const percentage = parseInt(percentageMatch[1], 10);
      const newScale = Math.max(0.25, Math.min(percentage / 100, 3.0));
      setScale(newScale);
      setScaleInput(`${Math.round(newScale * 100)}%`);
    } else {
      setScaleInput(`${Math.round(scale * 100)}%`);
    }
  };

  const handleScaleInputBlur = () => {
    changeValidateScale();
  };

  const handleScaleInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      changeValidateScale();
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await fetch(url.download);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename || url.filename || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <>
      <div
        className={clsx('pdfjs-viewer-container', className)}
        style={{ ...style, width, height }}
        {...props}
      >
        <div className="pdfjs-viewer-toolbar">
          <div className="toolbar-left">
            <button
              type="button"
              disabled={!PDFComponents || pageNumber <= 1} //disable if not loaded
              onClick={() => changePage(-1)}
              title="Previous Page"
            >
              <ChevronLeftIcon />
            </button>

            <button
              type="button"
              disabled={!PDFComponents || pageNumber >= numPages} //disable if not loaded
              onClick={() => changePage(1)}
              title="Next Page"
            >
              <ChevronRightIcon />
            </button>
            <input
              type="text"
              value={pageInput}
              onChange={handlePageInputChange}
              onBlur={handlePageInputBlur}
              onKeyDown={handlePageInputKeyDown}
              disabled={!PDFComponents || numPages === 0} //disable if not loaded
            />
            <span> / {numPages || '—'}</span>
          </div>
          <div className="toolbar-center">
            <button type="button" onClick={zoomOut} title="Zoom Out">
              -
            </button>

            <button type="button" onClick={zoomIn} title="Zoom In">
              +
            </button>
            <input
              type="text"
              value={scaleInput}
              onChange={handleScaleInputChange}
              onBlur={handleScaleInputBlur}
              onKeyDown={handleScaleInputKeyDown}
            />
            <button type="button" onClick={resetZoom} title="Reset Zoom">
              ⊙
            </button>
          </div>
          <div className="toolbar-right">
            <button type="button" onClick={downloadPDF} title="Download PDF">
              <DownloadIcon />
            </button>
          </div>
        </div>
        <div className="pdfjs-viewer-document">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error('Error while loading PDF document:', {
                error,
                errorMessage: error?.message,
                errorName: error?.name,
                url,
              });
            }}
            loading={<div className="pdf-loading">Loading PDF...</div>}
            noData={<div className="pdf-error">No PDF file specified</div>}
            error={
              <div className="pdf-error">
                Failed to load PDF. Please contact us
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>
        </div>
      </div>
      <Button
        className="pdfobject-viewer-download-button"
        text="Download document"
        title="Download document"
        onClick={downloadPDF}
      />
    </>
  );
};
export default PDFJSViewer;
